var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal, findDOMNode } from 'react-dom';
import { calculateStyle, getWindowSize, selectBestPreset, usePortalDOMNodeRef } from './helpers';
import { isComponent, isHTMLElement } from "../helpers/reflect";
import { useOutSideClick } from "../helpers/useOutSideClick";
import elementResizeEvent from 'element-resize-event';
import { useZIndexManager } from "../helpers/zIndexManager";
var selectSupportValue = function (values) {
    for (var value in values) {
        var element = document.createElement('b');
        element.style.display = value;
        if (element.style.display === value) {
            return value;
        }
    }
    return values[0];
};
var INLINE_FLEX_VALUE = selectSupportValue([
    'inline-flex',
    '-ms-inline-flexbox',
    '-webkit-inline-box'
]);
export var useMeasureClientRect = function (ref) {
    var listenersRef = useRef([]);
    var domNodeRef = ref || useRef(null);
    var getDOMNode = function () {
        var domNode = isHTMLElement(domNodeRef.current)
            ? domNodeRef.current
            : // eslint-disable-next-line react/no-find-dom-node
                findDOMNode(isComponent(domNodeRef.current) ? domNodeRef.current : null);
        return domNode;
    };
    var measure = function () {
        if (!domNodeRef.current) {
            return null;
        }
        var domNode = getDOMNode();
        if (!domNode || typeof domNode.getBoundingClientRect !== 'function') {
            return null;
        }
        var rect = domNode.getBoundingClientRect();
        return {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
        };
    };
    useEffect(function () {
        var domNode = getDOMNode();
        var handler = function () {
            listenersRef.current.forEach(function (cb) { return cb(); });
        };
        if (domNode) {
            elementResizeEvent(domNode, handler);
        }
        return function () {
            if (domNode) {
                elementResizeEvent.unbind(domNode, handler);
            }
        };
    }, []);
    return [
        domNodeRef,
        {
            measure: measure,
            getDOMNode: getDOMNode,
            listenResize: function (cb) {
                listenersRef.current.push(cb);
                return function () {
                    listenersRef.current = listenersRef.current.filter(function (i) { return i !== cb; });
                };
            }
        }
    ];
};
var PopoverContext = React.createContext({
    getTargetElements: null
});
function PopoverZStackManagerContainer(_a) {
    var children = _a.children, onKeyPress = _a.onKeyPress, getElements = _a.getElements, isActive = _a.isActive, onOutsideClick = _a.onOutsideClick;
    useZIndexManager({
        onKeyPress: onKeyPress
    });
    var childrenContextRef = useRef({
        getTargetElements: null
    });
    var popoverContext = useContext(PopoverContext);
    useEffect(function () {
        popoverContext.getTargetElements = function () { return getElements(); };
        return function () {
            popoverContext.getTargetElements = null;
        };
    }, []);
    useOutSideClick({
        isActive: isActive,
        getElements: function () {
            var _a, _b, _c;
            return __spreadArrays(getElements(), ((_c = (_b = (_a = childrenContextRef.current) === null || _a === void 0 ? void 0 : _a.getTargetElements) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : []));
        },
        onClick: function (e) {
            onOutsideClick === null || onOutsideClick === void 0 ? void 0 : onOutsideClick(e);
        }
    });
    return React.createElement(PopoverContext.Provider, { value: childrenContextRef.current }, children || null);
}
export var usePopover = function (_a) {
    var config = _a.config, _b = _a.isActive, isActive = _b === void 0 ? true : _b, _c = _a.viewportAccuracyFactor, viewportAccuracyFactor = _c === void 0 ? 1 : _c, _d = _a.zIndex, zIndex = _d === void 0 ? 9999 : _d, target = _a.target, render = _a.render, ref = _a.ref, onKeyPress = _a.onKeyPress, onOutsideClick = _a.onOutsideClick, _e = _a.getAdditionalContentNode, getAdditionalContentNode = _e === void 0 ? function () { return []; } : _e;
    var containerRef = useRef(null);
    containerRef = ref ? ref : containerRef;
    var portalDOMNodeRef = usePortalDOMNodeRef(zIndex);
    var _f = useMeasureClientRect(), popoverRef = _f[0], popoverRectActions = _f[1];
    var updateStyle = function () {
        if (!isActive) {
            return;
        }
        var actualPopoverRect = popoverRectActions.measure();
        var actualTargetRect = target.measure();
        var portalPosition = portalDOMNodeRef.current
            ? portalDOMNodeRef.current.getBoundingClientRect()
            : null;
        if (containerRef.current && actualTargetRect && portalPosition && actualPopoverRect) {
            var preset = selectBestPreset({
                presets: config,
                targetRect: actualTargetRect,
                popoverRect: actualPopoverRect,
                windowSize: getWindowSize(),
                portalPosition: portalPosition,
                viewportAccuracyFactor: viewportAccuracyFactor
            });
            Object.assign(containerRef.current.style, calculateStyle({
                preset: preset,
                targetRect: actualTargetRect,
                popoverRect: actualPopoverRect,
                portalPosition: portalPosition,
                offset: { top: preset.offsetY || 0, left: preset.offsetX || 0 }
            }));
        }
    };
    useEffect(function () {
        var removeListenResize;
        if (target.listenResize) {
            removeListenResize = target.listenResize(function () {
                updateStyle();
            });
        }
        if (typeof window !== 'undefined') {
            var listener_1 = function () {
                updateStyle();
            };
            window.addEventListener('resize', listener_1, true);
            window.addEventListener('scroll', listener_1, true);
            return function () {
                if (removeListenResize) {
                    removeListenResize();
                }
                window.removeEventListener('resize', listener_1, true);
                window.removeEventListener('scroll', listener_1, true);
            };
        }
        return function () {
            if (removeListenResize) {
                removeListenResize();
            }
        };
    }, [isActive]);
    if (!portalDOMNodeRef.current) {
        return null;
    }
    useLayoutEffect(function () {
        updateStyle();
    });
    useEffect(function () {
        setTimeout(function () {
            updateStyle();
        }, 0);
    });
    if (!isActive) {
        return null;
    }
    return createPortal(React.createElement(PopoverZStackManagerContainer, {
        onKeyPress: onKeyPress,
        isActive: isActive && !!onOutsideClick,
        getElements: function () {
            return __spreadArrays([containerRef.current], getAdditionalContentNode()).filter(Boolean);
        },
        onOutsideClick: onOutsideClick
    }, React.createElement('div', {
        tabIndex: -1,
        'data-selector-id': 'popover-container',
        'aria-modal': 'true',
        style: { outline: 'none', zIndex: 9999 },
        ref: function (node) {
            containerRef.current = node;
            if (node) {
                Object.assign(containerRef.current.style, {
                    display: INLINE_FLEX_VALUE,
                    visibility: 'hidden'
                });
            }
        }
    }, render(popoverRef))), portalDOMNodeRef.current);
};
//# sourceMappingURL=hooks.js.map